import { useCallback, useEffect } from "react";
import { postMmkItemAction } from "../axios/mmk";
import useAppStore from "../stores/appStore";
import { useMmkStore } from "../stores/mmkStore";
import { MmkItem } from "../types/mmk";

interface UseMmkItemsResult {
  items: MmkItem[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  scopeKey: string;
  toggleMastered: (id: string) => Promise<void>;
  markAsMastered: (id: string) => Promise<void>;
  togglePin: (id: string) => Promise<void>;
  toggleReviewLater: (id: string) => Promise<void>;
  markQuizWrong: (id: string) => Promise<void>;
}

export const useMmkItems = (): UseMmkItemsResult => {
  const { focus, domain } = useAppStore();
  const {
    items,
    totalCount,
    loading,
    error,
    fetchItems,
    updateItem,
    addQuizWrongOverride,
    removeQuizWrongOverride,
  } = useMmkStore();

  useEffect(() => {
    if (!focus || !domain) return;
    fetchItems();
  }, [fetchItems, focus, domain]);

  const toggleMastered = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      try {
        const response = await postMmkItemAction(id, "mark_as_known");
        const newMastered =
          typeof response.item?.is_mastered === "boolean"
            ? response.item.is_mastered
            : !(item?.is_mastered ?? false);
        if (newMastered) removeQuizWrongOverride(id);
        updateItem(
          id,
          response.item
            ? { ...response.item }
            : {
                is_mastered: newMastered,
                status: newMastered ? "known" : "default",
                last_action_at: new Date().toISOString(),
              },
        );
      } catch {
        // keep rollback or show toast
      }
    },
    [items, updateItem, removeQuizWrongOverride],
  );

  const markAsMastered = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      if (item?.is_mastered) return;
      try {
        const response = await postMmkItemAction(id, "mark_as_known");
        removeQuizWrongOverride(id);
        updateItem(
          id,
          response.item
            ? { ...response.item }
            : {
                is_mastered: true,
                status: "known",
                last_action_at: new Date().toISOString(),
              },
        );
      } catch {
        // keep rollback or show toast
      }
    },
    [items, updateItem, removeQuizWrongOverride],
  );

  const togglePin = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      try {
        const response = await postMmkItemAction(id, "pin");
        const newPinned = !item.is_pinned;
        const base: Partial<MmkItem> = response.item
          ? { ...response.item }
          : {
              is_pinned: newPinned,
              status: newPinned
                ? "pinned"
                : item.is_mastered
                  ? "known"
                  : item.is_review_later
                    ? "review_later"
                    : "default",
              last_action_at: new Date().toISOString(),
            };
        if (newPinned) {
          const hasMastered = base.is_mastered ?? item.is_mastered ?? false;
          const hasReview = base.is_review_later ?? item.is_review_later ?? false;
          if (!hasMastered && !hasReview) {
            base.is_review_later = true;
          }
        }
        updateItem(id, base);
      } catch {
        // keep rollback or show toast
      }
    },
    [items, updateItem],
  );

  const toggleReviewLater = useCallback(
    async (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item) return;
      try {
        const response = await postMmkItemAction(id, "review_later");
        const newReviewLater = !item.is_review_later;
        updateItem(
          id,
          response.item
            ? { ...response.item }
            : {
                is_review_later: newReviewLater,
                status: newReviewLater
                  ? "review_later"
                  : item.is_mastered
                    ? "known"
                    : item.is_pinned
                      ? "pinned"
                      : "default",
                last_action_at: new Date().toISOString(),
              },
        );
      } catch {
        // keep rollback or show toast
      }
    },
    [items, updateItem],
  );

  const markQuizWrong = useCallback(
    async (id: string) => {
      try {
        const response = await postMmkItemAction(id, "quiz_wrong");
        addQuizWrongOverride(id);
        const updates = response.item
          ? { ...response.item, is_mastered: false, is_review_later: true }
          : {
              is_review_later: true,
              is_mastered: false,
              last_action_at: new Date().toISOString(),
            };
        updateItem(id, updates);
      } catch {
        // keep rollback or show toast
      }
    },
    [updateItem, addQuizWrongOverride],
  );

  return {
    items,
    totalCount,
    loading,
    error,
    scopeKey: `${focus}::${domain}`,
    toggleMastered,
    markAsMastered,
    togglePin,
    toggleReviewLater,
    markQuizWrong,
  };
};
